import {
  Box,
  Typography,
  CircularProgress, Dialog, IconButton,
} from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/state-management/state-provider';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { Site, ContentType } from 'src/api/Api';
import { ProjectWithSites } from 'src/state-management/state';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import Header from 'src/view/components/navigation/Header';
import { Close as CloseIcon } from '@mui/icons-material';
// import { ChatBubble } from '@mui/icons-material';

import './style.scss';
// import siteMockData from 'src/resources/mock/site_mock_data.json';
import {
  GUNDRE_SCORECARD_PROJECT_ID,
  gundreScorecards,
  // resolveGundrePlotKey,
  ScorecardIndicator,
} from 'src/resources/mock/gundre_scorecard_data';
import AudiomothDetectionsPopup from 'src/view/components/AudiomothDetectionsPopup';
import HydroacousticDetectionsPopup from 'src/view/components/HydroacousticDetectionsPopup';
import CameraDetectionPopup from 'src/view/components/CameraDetectionPopup';
import SoilSensorResultsPopup from 'src/view/components/SoilSensorResultsPopup';
import UploadAudioModal from 'src/view/tool/bioacoustic/components/UploadAudioModal';
import { DeploymentMarkerData } from 'src/view/Project/InteractiveMap';

// Import refactored components
import LeftPanel from 'src/view/Project/LeftPanel';
import InteractiveMap from 'src/view/Project/InteractiveMap';
import RightPanel from 'src/view/Project/RightPanel';
import DataRoom from './DataRoom/DataRoom';
import ProjectReadMoreDialog, { DeploymentImageItem } from 'src/view/Project/ProjectReadMoreDialog';
import ShowcaseGalleryDialog from 'src/view/Project/ShowcaseGalleryDialog';
import CreateProjectSessionDialog, {
  CreateProjectSessionPayload,
} from 'src/view/Project/CreateProjectSessionDialog';

// Parsing WKT point coordinates [longitude, latitude]
const parseWKT = (wkt: string | null | undefined): [number, number] | null => {
  if (!wkt) return null;
  const match = wkt.match(/POINT\s*\(([^)]+)\)/i);
  if (match) {
    const coords = match[1].trim().split(/\s+/);
    if (coords.length === 2) {
      const lng = parseFloat(coords[0]);
      const lat = parseFloat(coords[1]);
      if (!isNaN(lng) && !isNaN(lat)) {
        return [lng, lat];
      }
    }
  }
  return null;
};

// Helper to parse WKT Polygon/MultiPolygon to a bounding box [[minLng, minLat], [maxLng, maxLat]]
const parseWKTPolygonToBBox = (wkt: string): [[number, number], [number, number]] | null => {
  const coordRegex = /[-\d.]+\s+[-\d.]+/g;
  const matches = wkt.match(coordRegex);
  if (!matches) return null;
  const coords = matches.map(m => m.trim().split(/\s+/).map(Number));

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  coords.forEach(([lng, lat]) => {
    if (!isNaN(lng) && !isNaN(lat)) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  });

  if (minLng === Infinity || minLat === Infinity) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
};

const ProjectDetail = () => {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();

  const { selectedProject } = state;
  const projectDetails = selectedProject?.projectDetails as ProjectWithSites | undefined;

  // Local States
  const [sites, setSites] = useState<Site[]>(projectDetails?.sites || []);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [activeLeftTab, setActiveLeftTab] = useState<'layers' | 'reports' | 'dataroom'>('layers');
  const [activeRightTab, setActiveRightTab] = useState<'all' | 'nature' | 'climate'>('all');
  const [createSessionOpen, setCreateSessionOpen] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [readMoreOpen, setReadMoreOpen] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  const [projectMedia, setProjectMedia] = useState<any[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [showcaseModalOpen, setShowcaseModalOpen] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [candidatePage, setCandidatePage] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedCandidates, setSelectedCandidates] = useState<Record<string, boolean>>({});
  const [candidateCaptions, setCandidateCaptions] = useState<Record<string, string>>({});
  const [candidateFileNames, setCandidateFileNames] = useState<Record<string, string>>({});

  // Map configuration states
  const [isBasemapOn, setIsBasemapOn] = useState(true);
  const [isFeatureLayerOn, setIsFeatureLayerOn] = useState(false);

  // Analysis Metric summaries fetched from API
  const [bioSummary, setBioSummary] = useState<Record<string, unknown> | null>(null);
  const [envSummary, setEnvSummary] = useState<Record<string, unknown> | null>(null);

  // Selected site for right-panel context
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [isSiteLevel, setIsSiteLevel] = useState(false);

  // Baseline ecological scorecard data (Gundre plots project only)
  const [scorecard, setScorecard] = useState<ScorecardIndicator[] | null>(null);

  // Audiomoth popup trigger states
  const [audiomothPopupOpen, setAudiomothPopupOpen] = useState(false);
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentMarkerData | null>(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Map ref
  const map = useRef<mapboxgl.Map | null>(null);

  // Panel resizing states
  const [leftPanelWidth, setLeftPanelWidth] = useState<number | undefined>(undefined);
  const [rightPanelWidth, setRightPanelWidth] = useState<number | undefined>(undefined);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizingLeft) {
        let newWidth = e.clientX;
        const maxWidth = window.innerWidth <= 1200 ? 280 : 320;
        if (newWidth < 0) newWidth = 0;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setLeftPanelWidth(newWidth);
        if (map.current) {
            map.current.resize();
        }
      } else if (isResizingRight) {
        let newWidth = window.innerWidth - e.clientX;
        const maxWidth = window.innerWidth <= 1200 ? 480 : 600;
        if (newWidth < 0) newWidth = 0;
        if (newWidth > maxWidth) newWidth = maxWidth;
        setRightPanelWidth(newWidth);
        if (map.current) {
            map.current.resize();
        }
      }
    };

    const handleMouseUp = () => {
      if (isResizingLeft) setIsResizingLeft(false);
      if (isResizingRight) setIsResizingRight(false);
    };

    if (isResizingLeft || isResizingRight) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingLeft, isResizingRight]);

  const isCorbettProject = projectDetails?.project_name?.toLowerCase().includes('corbett') || false;
  const isGundreScorecardProject = projectDetails?.project_id === GUNDRE_SCORECARD_PROJECT_ID;

  // Default to Plot 1's scorecard as soon as this project loads
  useEffect(() => {
    if (isGundreScorecardProject) {
      setScorecard(gundreScorecards['Gundre Plot 1']);
    } else {
      setScorecard(null);
    }
  }, [isGundreScorecardProject]);

  // Fetch sites for single project if empty
  useEffect(() => {
    const fetchSites = async () => {
      if (!projectDetails?.project_id) return;
      if (projectDetails.sites && projectDetails.sites.length > 0) {
        setSites(projectDetails.sites);
        return;
      }

      setIsLoadingSites(true);
      try {
        const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(
          projectDetails.project_id
        );
        const fetchedSites = response.data || [];
        setSites(fetchedSites);
        dispatch({
          type: 'SET_PROJECT_SITES',
          payload: fetchedSites,
        });
      } catch (error) {
        console.error('Failed to load sites:', error);
        showSnackbar({
          message: 'Failed to load sites for this project',
          type: snackbarType.ERROR,
        });
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails?.project_id, dispatch, showSnackbar, snackbarType.ERROR]);

  // Fetch project-level Biodiversity and Environmental analysis summaries
  useEffect(() => {
    if (!projectDetails?.project_id) return;

    const fetchSummaries = async () => {
      try {
        const bioRes = await clientService.api.readBiodiversityProjectSummaryApiV1ProjectsProjectIdBiodiversitySummaryGet(
          projectDetails.project_id
        );
        setBioSummary(bioRes.data as unknown as Record<string, unknown>);
      } catch (err) {
        console.error('Failed to fetch bio summary', err);
      }

      try {
        const envRes = await clientService.api.readEnvironmentalProjectSummaryApiV1ProjectsProjectIdEnvironmentalSummaryGet(
          projectDetails.project_id
        );
        setEnvSummary(envRes.data as unknown as Record<string, unknown>);
      } catch (err) {
        console.error('Failed to fetch env summary', err);
      }
    };

    fetchSummaries();
  }, [projectDetails?.project_id]);

  // Fetch project media asynchronously on page mount
  const preFetchMedia = useCallback(async () => {
    if (!projectDetails?.project_id) return;
    try {
      const response = await clientService.api.getProjectMediaListApiV1AdminProjectsProjectIdMediaGet(
        projectDetails.project_id
      );
      const mediaList = response.data || [];
      setProjectMedia(mediaList);

      // Async resolve download URLs in background
      const urlsMap: Record<string, string> = {};
      await Promise.all(
        mediaList.map(async (media: any) => {
          try {
            const urlRes = await clientService.api.getProjectMediaDownloadUrlApiV1AdminProjectMediaMediaIdDownloadUrlGet(
              media.media_id
            );
            urlsMap[media.media_id] = urlRes.data.download_url;
          } catch (err) {
            console.error(`Failed to get download URL for media ${media.media_id}`, err);
          }
        })
      );
      setMediaUrls(urlsMap);
    } catch (err) {
      console.error("Failed to load project media:", err);
    }
  }, [projectDetails?.project_id]);

  useEffect(() => {
    preFetchMedia();
  }, [preFetchMedia]);

  // Deployment & Retrieval Images state
  const [deploymentImages, setDeploymentImages] = useState<DeploymentImageItem[]>([]);
  const [isLoadingDeploymentImages, setIsLoadingDeploymentImages] = useState<boolean>(false);

  // Helper to resolve raw GCS blob paths to presigned download URLs via API
  const resolveImageUrl = async (depId: string, urlOrPath: string): Promise<string> => {
    if (!urlOrPath || typeof urlOrPath !== 'string') return '';
    const trimmed = urlOrPath.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
      return trimmed;
    }
    try {
      const res = await clientService.request<{ download_url: string }, any>({
        path: `/api/v1/admin/deployments/${depId}/assessment-media-download-url`,
        method: 'POST',
        body: { media_url: trimmed },
        format: 'json',
        secure: true,
      });
      return res.data?.download_url || trimmed;
    } catch (err) {
      console.warn(`Failed to resolve presigned download URL for ${trimmed}`, err);
      return trimmed;
    }
  };

  // Fetch deployment_info and retrieval_info images
  const fetchDeploymentImages = useCallback(async () => {
    if (!projectDetails?.project_id) return;
    setIsLoadingDeploymentImages(true);
    try {
      // 0. Primary: Call dedicated project deployment media API
      try {
        const directRes: any = await clientService.request({
          path: `/api/v1/projects/${projectDetails.project_id}/deployment-media`,
          method: 'GET',
          format: 'json',
        });
        const mediaItems = directRes.data || directRes;
        if (Array.isArray(mediaItems) && mediaItems.length > 0) {
          const formatted: DeploymentImageItem[] = mediaItems.map((item: any) => ({
            id: item.id || `media-${Math.random()}`,
            url: item.url,
            title: item.description || item.deployment_name || 'Deployment Photo',
            source: item.type?.toLowerCase().includes('retrieval') ? 'retrieval_info' : 'deployment_info',
            category: item.type || 'Field Photo',
            deploymentName: item.deployment_name || 'Deployment',
          }));
          setDeploymentImages(formatted);
          setIsLoadingDeploymentImages(false);
          return;
        }
      } catch (err) {
        console.warn('Dedicated deployment media API failed or not available, falling back to multi-tier fetcher', err);
      }

      // 1. Fallback: Get deployments list for project via map data API or Site Admin Details
      let deploymentsList: any[] = [];

      try {
        const mapRes = await clientService.api.getProjectMapDataApiV1ProjectsProjectIdMapGet(
          projectDetails.project_id
        );
        deploymentsList = mapRes.data?.deployments || [];
      } catch (e) {
        console.warn('Map data API not available for project status or error, trying site details fallback', e);
      }

      // Fallback 1: Extract deployments via site details API for each site in project
      if (!deploymentsList.length) {
        try {
          let sites: any[] = projectDetails?.sites || [];
          if (!sites.length) {
            const sitesRes = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(
              projectDetails.project_id
            );
            sites = sitesRes.data || [];
          }

          if (sites.length) {
            await Promise.all(
              sites.map(async (s: any) => {
                const siteId = s.site_id;
                if (!siteId) return;
                try {
                  const siteDetailRes = await clientService.api.getSiteDetailsAdminApiV1AdminSitesSiteIdGet(siteId);
                  const siteDetail = siteDetailRes.data;
                  if (siteDetail?.locations) {
                    siteDetail.locations.forEach((loc: any) => {
                      if (loc.deployments) {
                        deploymentsList.push(...loc.deployments);
                      }
                    });
                  }
                } catch (err) {
                  console.warn(`Could not fetch site details for site ${siteId}`, err);
                }
              })
            );
          }
        } catch (err) {
          console.warn('Failed fallback site retrieval:', err);
        }
      }

      // Fallback 2: Check projectDetails.sites tree directly
      if (!deploymentsList.length && projectDetails?.sites) {
        projectDetails.sites.forEach((site: any) => {
          if (site.locations) {
            site.locations.forEach((loc: any) => {
              if (loc.deployments) {
                deploymentsList.push(...loc.deployments);
              }
            });
          }
        });
      }

      if (!deploymentsList.length) {
        setDeploymentImages([]);
        return;
      }

      // Deduplicate deployments by deployment_id
      const uniqueDeploymentsMap = new Map<string, any>();
      deploymentsList.forEach((dep: any) => {
        if (dep?.deployment_id && !uniqueDeploymentsMap.has(dep.deployment_id)) {
          uniqueDeploymentsMap.set(dep.deployment_id, dep);
        }
      });
      const uniqueDeployments = Array.from(uniqueDeploymentsMap.values());

      // 2. Fetch comparison info & deployment details for each deployment
      const images: DeploymentImageItem[] = [];

      await Promise.all(
        uniqueDeployments.map(async (dep: any) => {
          const depId = dep.deployment_id;
          const depName = dep.deployment_name || 'Deployment';
          if (!depId) return;

          // Check for inaccessible_photo_url on deployment
          if (dep.inaccessible_photo_url && typeof dep.inaccessible_photo_url === 'string') {
            const resolved = await resolveImageUrl(depId, dep.inaccessible_photo_url);
            if (resolved) {
              images.push({
                id: `dep-${depId}-inaccessible`,
                url: resolved,
                title: 'Inaccessible Site Photo',
                source: 'deployment_info',
                category: 'Site Accessibility',
                deploymentName: depName,
              });
            }
          }

          // Fetch comparison data (deployment_info & retrieval_info)
          try {
            const compRes = await clientService.api.getDeploymentComparisonApiApiV1AdminDeploymentsDeploymentIdComparisonGet(
              depId
            );
            const compData = compRes.data;
            const depInfo = compData?.deployment_info;
            const retInfo = compData?.retrieval_info;

            const processInfo = async (info: any, sourceType: 'deployment_info' | 'retrieval_info') => {
              if (!info) return;

              // 1. photo_urls dict
              if (info.photo_urls && typeof info.photo_urls === 'object') {
                await Promise.all(
                  Object.entries(info.photo_urls).map(async ([key, val]) => {
                    if (typeof val === 'string' && val.trim()) {
                      const resolvedUrl = await resolveImageUrl(depId, val);
                      if (resolvedUrl) {
                        images.push({
                          id: `${sourceType}-${depId}-photo-${key}`,
                          url: resolvedUrl,
                          title: key.replace(/_/g, ' '),
                          source: sourceType,
                          category: 'Photo',
                          deploymentName: depName,
                        });
                      }
                    }
                  })
                );
              }

              // 2. flora_assessment
              if (info.flora_assessment && typeof info.flora_assessment === 'object') {
                const floraImgs = info.flora_assessment.image_urls || info.flora_assessment.images || info.flora_assessment.photos;
                if (Array.isArray(floraImgs)) {
                  await Promise.all(
                    floraImgs.map(async (val: any, idx: number) => {
                      if (typeof val === 'string' && val.trim()) {
                        const resolvedUrl = await resolveImageUrl(depId, val);
                        if (resolvedUrl) {
                          images.push({
                            id: `${sourceType}-${depId}-flora-${idx}`,
                            url: resolvedUrl,
                            title: `Flora Assessment ${idx + 1}`,
                            source: sourceType,
                            category: 'Flora Assessment',
                            deploymentName: depName,
                          });
                        }
                      }
                    })
                  );
                }
              }

              // 3. fauna_assessment
              if (info.fauna_assessment && typeof info.fauna_assessment === 'object') {
                const faunaImgs = info.fauna_assessment.image_urls || info.fauna_assessment.images || info.fauna_assessment.photos;
                if (Array.isArray(faunaImgs)) {
                  await Promise.all(
                    faunaImgs.map(async (val: any, idx: number) => {
                      if (typeof val === 'string' && val.trim()) {
                        const resolvedUrl = await resolveImageUrl(depId, val);
                        if (resolvedUrl) {
                          images.push({
                            id: `${sourceType}-${depId}-fauna-${idx}`,
                            url: resolvedUrl,
                            title: `Fauna Assessment ${idx + 1}`,
                            source: sourceType,
                            category: 'Fauna Assessment',
                            deploymentName: depName,
                          });
                        }
                      }
                    })
                  );
                }
              }

              // 4. habitat_condition
              if (info.habitat_condition && typeof info.habitat_condition === 'object') {
                const habitatImgs = info.habitat_condition.image_urls || info.habitat_condition.photo_urls || info.habitat_condition.images;
                if (Array.isArray(habitatImgs)) {
                  await Promise.all(
                    habitatImgs.map(async (val: any, idx: number) => {
                      if (typeof val === 'string' && val.trim()) {
                        const resolvedUrl = await resolveImageUrl(depId, val);
                        if (resolvedUrl) {
                          images.push({
                            id: `${sourceType}-${depId}-habitat-${idx}`,
                            url: resolvedUrl,
                            title: `Habitat Photo ${idx + 1}`,
                            source: sourceType,
                            category: 'Habitat Condition',
                            deploymentName: depName,
                          });
                        }
                      }
                    })
                  );
                }
              }
            };

            await processInfo(depInfo, 'deployment_info');
            await processInfo(retInfo, 'retrieval_info');
          } catch (err) {
            console.warn(`Could not fetch comparison info for deployment ${depId}`, err);
          }

          // Fetch deployment details to check attached image files
          try {
            const depDetailRes = await clientService.api.getDeploymentDetailsAdminApiV1AdminDeploymentsDeploymentIdGet(depId);
            const files = (depDetailRes.data as any)?.files || [];
            const imageExts = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.tiff'];
            await Promise.all(
              files.map(async (fileObj: any, fIdx: number) => {
                const url = fileObj.url || fileObj.gcs_path;
                const fileName = fileObj.name || `File ${fIdx + 1}`;
                if (typeof url === 'string' && url.trim()) {
                  const lowerUrl = url.toLowerCase();
                  const isImg = imageExts.some((ext) => lowerUrl.includes(ext)) || fileObj.content_type?.startsWith('image/');
                  if (isImg) {
                    const resolvedUrl = await resolveImageUrl(depId, url);
                    if (resolvedUrl) {
                      images.push({
                        id: `dep-${depId}-file-${fIdx}`,
                        url: resolvedUrl,
                        title: fileName,
                        source: 'deployment_info',
                        category: 'Deployment File',
                        deploymentName: depName,
                      });
                    }
                  }
                }
              })
            );
          } catch (err) {
            // Non-critical if deployment files call fails or not accessible
          }
        })
      );

      setDeploymentImages(images);
    } catch (err) {
      console.error('Failed to fetch deployment images:', err);
    } finally {
      setIsLoadingDeploymentImages(false);
    }
  }, [projectDetails?.project_id, projectDetails?.sites]);

  useEffect(() => {
    if (readMoreOpen && projectDetails?.project_id) {
      void fetchDeploymentImages();
    }
  }, [readMoreOpen, projectDetails?.project_id, fetchDeploymentImages]);

  // Fetch candidates for the showcase management modal
  useEffect(() => {
    if (!showcaseModalOpen || !projectDetails?.project_id) return;

    const fetchCandidates = async () => {
      setIsLoadingCandidates(true);
      try {
        const limit = 6;
        const skip = (candidatePage - 1) * limit;
        const response = await clientService.request<any, any>({
          path: `/api/v1/projects/${projectDetails.project_id}/media/showcase/candidates`,
          method: 'GET',
          query: { skip, limit },
          format: 'json',
          secure: true,
        });
        const fetchedCandidates = response.data?.candidates || [];
        setCandidates(fetchedCandidates);
        setTotalCandidates(response.data?.count || 0);

        // Pre-populate selection states
        setSelectedCandidates((prev) => {
          const next = { ...prev };
          fetchedCandidates.forEach((c: any) => {
            if (next[c.gcs_path] === undefined) {
              next[c.gcs_path] = c.is_showcased;
            }
          });
          return next;
        });

        // Initialize file names
        setCandidateFileNames((prev) => {
          const next = { ...prev };
          fetchedCandidates.forEach((c: any) => {
            if (next[c.gcs_path] === undefined) {
              next[c.gcs_path] = c.file_name || '';
            }
          });
          return next;
        });

        // Initialize captions
        setCandidateCaptions((prev) => {
          const next = { ...prev };
          fetchedCandidates.forEach((c: any) => {
            if (next[c.gcs_path] === undefined) {
              next[c.gcs_path] = c.caption || '';
            }
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load showcase candidates:', err);
      } finally {
        setIsLoadingCandidates(false);
      }
    };

    void fetchCandidates();
  }, [showcaseModalOpen, candidatePage, projectDetails?.project_id]);

  const handleSaveShowcase = async () => {
    if (!projectDetails?.project_id) return;
    const pId = projectDetails.project_id;

    const toPromote: any[] = [];
    const toDemote: string[] = [];

    Object.keys(selectedCandidates).forEach((gcsPath) => {
      const isSelected = selectedCandidates[gcsPath];
      const isAlreadyShowcased = projectMedia.some((m) => m.gcs_path === gcsPath);

      if (isSelected && !isAlreadyShowcased) {
        toPromote.push({
          gcs_path: gcsPath,
          caption: candidateCaptions[gcsPath] || '',
          file_name: candidateFileNames[gcsPath] || gcsPath.split('/').pop() || '',
        });
      } else if (!isSelected && isAlreadyShowcased) {
        toDemote.push(gcsPath);
      }
    });

    try {
      if (toPromote.length > 0) {
        await clientService.request({
          path: `/api/v1/projects/${pId}/media/showcase`,
          method: 'POST',
          body: { photos: toPromote },
          type: ContentType.Json,
          format: 'json',
          secure: true,
        });
      }

      if (toDemote.length > 0) {
        await clientService.request({
          path: `/api/v1/projects/${pId}/media/showcase`,
          method: 'DELETE',
          body: { gcs_paths: toDemote },
          type: ContentType.Json,
          format: 'json',
          secure: true,
        });
      }

      showSnackbar({
        message: 'Showcase gallery updated successfully',
        type: snackbarType.SUCCESS,
      });

      setShowcaseModalOpen(false);
      await preFetchMedia();
    } catch (err: unknown) {
      const errObj = err as { status?: number; error?: unknown; data?: unknown };
      console.error('Failed to update showcase gallery:', err);
      console.error('Status:', errObj?.status);
      console.error('Error body:', errObj?.error ?? errObj?.data);
      showSnackbar({
        message: 'Failed to update showcase gallery',
        type: snackbarType.ERROR,
      });
    }
  };

  const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const err = error as {
      error?: { detail?: string | { msg?: string }[] };
      data?: { detail?: string | { msg?: string }[] };
      message?: string;
    };
    const detail = err?.error?.detail ?? err?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return err?.message || fallback;
  };

  const handleCreateProjectSession = async (payload: CreateProjectSessionPayload) => {
    if (!projectDetails?.project_id) return;

    setIsCreatingSession(true);
    try {
      await clientService.request({
        path: `/api/v1/projects/${projectDetails.project_id}/sessions`,
        method: 'POST',
        body: payload,
        type: ContentType.Json,
        format: 'json',
        secure: true,
      });

      showSnackbar({
        message: 'Project session created successfully',
        type: snackbarType.SUCCESS,
      });
      setCreateSessionOpen(false);
    } catch (err) {
      console.error('Failed to create project session:', err);
      showSnackbar({
        message: getApiErrorMessage(err, 'Failed to create project session'),
        type: snackbarType.ERROR,
      });
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Fetch site-level summaries when a site is selected (Commented out per user request)
  /*
  const fetchSiteSummaries = async (site: Site) => {
    setSelectedSite(site);
    setIsSiteLevel(true);

    if (isGundreScorecardProject) {
      const plotKey = resolveGundrePlotKey(site.site_name);
      setScorecard(gundreScorecards[plotKey ?? 'Gundre Plot 1']);
      return;
    }

    const siteKey = site.site_name.toLowerCase().includes('dwarik')
      ? 'dwarikanagar'
      : site.site_name.toLowerCase().includes('maipith') || site.site_name.toLowerCase().includes('maithpith')
        ? 'maithpith'
        : site.site_name.toLowerCase().includes('tiphli') || site.site_name.toLowerCase().includes('tipli') || site.site_name.toLowerCase().includes('tipl')
          ? 'tiplighiri'
          : null;

    if (siteKey && siteKey in siteMockData) {
      const mock = siteMockData[siteKey as keyof typeof siteMockData];
      setBioSummary(mock.bioSummary as unknown as Record<string, unknown>);
      setEnvSummary(mock.envSummary as unknown as Record<string, unknown>);
      return;
    }

    try {
      const bioRes = await clientService.api.readBiodiversitySiteDetailsApiV1SitesSiteIdBiodiversityGet(site.site_id);
      setBioSummary(bioRes.data as unknown as Record<string, unknown>);
    } catch (err) {
      console.error('Failed to fetch site bio summary', err);
    }
    try {
      const envRes = await clientService.api.readEnvironmentalSiteDetailsApiV1SitesSiteIdEnvironmentalGet(site.site_id);
      setEnvSummary(envRes.data as unknown as Record<string, unknown>);
    } catch (err) {
      console.error('Failed to fetch site env summary', err);
    }
  };
  */

  // Revert to project-level summaries
  const resetToProjectLevel = async () => {
    if (!projectDetails?.project_id) return;
    setSelectedSite(null);
    setIsSiteLevel(false);

    if (isGundreScorecardProject) {
      setScorecard(gundreScorecards['Gundre Plot 1']);
      return;
    }

    try {
      const bioRes = await clientService.api.readBiodiversityProjectSummaryApiV1ProjectsProjectIdBiodiversitySummaryGet(projectDetails.project_id);
      setBioSummary(bioRes.data as unknown as Record<string, unknown>);
    } catch (err) {
      console.error('Failed to reset bio summary', err);
    }
    try {
      const envRes = await clientService.api.readEnvironmentalProjectSummaryApiV1ProjectsProjectIdEnvironmentalSummaryGet(projectDetails.project_id);
      setEnvSummary(envRes.data as unknown as Record<string, unknown>);
    } catch (err) {
      console.error('Failed to reset env summary', err);
    }
  };

  const handleSiteClick = (site: Site) => {
    const centroid = parseWKT(site.centroid_wkt);
    if (centroid && map.current) {
      map.current.flyTo({ center: centroid, zoom: 12, duration: 1200 });
    }
    if (site.boundary && map.current) {
      try {
        let bbox: [[number, number], [number, number]] | null = null;
        if (typeof site.boundary === 'string') {
          const trimmed = site.boundary.trim();
          if (trimmed.toUpperCase().startsWith('POLYGON') || trimmed.toUpperCase().startsWith('MULTIPOLYGON')) {
            bbox = parseWKTPolygonToBBox(trimmed);
          } else {
            const geo = JSON.parse(trimmed);
            const tBbox = turf.bbox(geo);
            bbox = [[tBbox[0], tBbox[1]], [tBbox[2], tBbox[3]]];
          }
        } else {
          const tBbox = turf.bbox(site.boundary);
          bbox = [[tBbox[0], tBbox[1]], [tBbox[2], tBbox[3]]];
        }

        if (bbox) {
              map.current.resize();
          try {
            map.current.fitBounds(bbox, { padding: 40, maxZoom: 14, duration: 1200 });
          } catch (fitErr) {
            console.warn("fitBounds failed on site click:", fitErr);
          }
        }
      } catch (e) {
        console.error('Failed to fit bounds to site boundary', e);
      }
    }
    // void fetchSiteSummaries(site);
  };

  if (!projectDetails) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#070809">
        <CircularProgress />
        <Typography sx={{ ml: 2, color: '#fff' }}>Loading project details...</Typography>
      </Box>
    );
  }

  return (
    <Box className={`project-detail-map-dashboard ${isMapFullscreen ? 'is-map-fullscreen' : ''}`} sx={{ padding: 0, margin: 0 }}>
      {!isMapFullscreen && <Header />}

      {/* Main Panel Area */}
      <Box className={`dashboard-wrapper ${isResizingLeft || isResizingRight ? 'is-resizing' : ''}`} sx={{ padding: 0 }}>
        <LeftPanel
          projectDetails={projectDetails}
          sites={sites}
          isLoadingSites={isLoadingSites}
          activeLeftTab={activeLeftTab}
          setActiveLeftTab={setActiveLeftTab}
          selectedSite={selectedSite}
          onReadMoreClick={() => setReadMoreOpen(true)}
          onSiteClick={handleSiteClick}
          onNewSessionClick={() => setCreateSessionOpen(true)}
          width={leftPanelWidth}
        />

        <div
          className={`resizer ${isResizingLeft ? 'resizing' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); setIsResizingLeft(true); }}
        />

        <InteractiveMap
            projectDetails={projectDetails}
            sites={sites}
            isBasemapOn={isBasemapOn}
            setIsBasemapOn={setIsBasemapOn}
            isFeatureLayerOn={isFeatureLayerOn}
            setIsFeatureLayerOn={setIsFeatureLayerOn}
            mapRef={map}
            onFullscreenChange={setIsMapFullscreen}
            onDeviceClick={(siteName, markerData, deploymentId) => {
              setSelectedSiteName(siteName);
              setSelectedDeployment(markerData ?? null);
              setSelectedDeploymentId(deploymentId || markerData?.deployment_id || null);
              setAudiomothPopupOpen(true);
            }}
          />

        <div
          className={`resizer ${isResizingRight ? 'resizing' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); setIsResizingRight(true); }}
        />

        <RightPanel
          projectDetails={projectDetails}
          isSiteLevel={isSiteLevel}
          selectedSite={selectedSite}
          activeRightTab={activeRightTab}
          setActiveRightTab={setActiveRightTab}
          bioSummary={bioSummary}
          envSummary={envSummary}
          overviewSummary={null}
          onResetProjectView={() => void resetToProjectLevel()}
          isCorbettProject={isCorbettProject}
          scorecard={scorecard}
          width={rightPanelWidth}
        />

        {/* Floating Chat Bubble */}
        {/* <Box className="floating-chat-bubble">
          <ChatBubble />
        </Box> */}

        <ProjectReadMoreDialog
          open={readMoreOpen}
          onClose={() => setReadMoreOpen(false)}
          projectDetails={projectDetails}
          projectMedia={projectMedia}
          mediaUrls={mediaUrls}
          deploymentImages={deploymentImages}
          isLoadingDeploymentImages={isLoadingDeploymentImages}
          onManageGalleryClick={() => setShowcaseModalOpen(true)}
        />
        <CreateProjectSessionDialog
          open={createSessionOpen}
          projectName={projectDetails.project_name}
          isSubmitting={isCreatingSession}
          onClose={() => setCreateSessionOpen(false)}
          onCreate={handleCreateProjectSession}
        />

        {(() => {
          const dName = selectedDeployment?.deployment_name || '';
          const dId = selectedDeploymentId || selectedDeployment?.deployment_id;
          const dType = (selectedDeployment?.device_type || '').toLowerCase();
          const isCam = dType === 'camera' ||
                        dId === 'fd713f67-3684-4a00-a047-5fea7a44e42b' ||
                        dName === 'PMP-T0352' || dName === 'PMP-T035' ||
                        dName === 'PMP-T0171' || dName === 'PMP-T0612';
          const isHydro = dType === 'hydro' || dType === 'hydroacoustic' || dId === '462fb893-ec5b-429e-a470-9744b0493ab9' || dId === '6621d433-48bb-45a6-94b6-84948f136878';
          const isSoil = dType === 'soil_sensor' || dType === 'soil' || dName.toLowerCase().includes('soil');

          if (isSoil) {
            return (
              <SoilSensorResultsPopup
                open={audiomothPopupOpen}
                onClose={() => setAudiomothPopupOpen(false)}
                siteName={selectedSiteName}
                deployment={selectedDeployment}
                deploymentId={dId}
                projectId={projectDetails?.project_id}
              />
            );
          }

          if (isCam) {
            return (
              <CameraDetectionPopup
                open={audiomothPopupOpen}
                onClose={() => setAudiomothPopupOpen(false)}
                siteName={selectedSiteName}
                deployment={selectedDeployment}
                deploymentId={dId}
              />
            );
          }

          if (isHydro) {
            return (
              <HydroacousticDetectionsPopup
                open={audiomothPopupOpen}
                onClose={() => setAudiomothPopupOpen(false)}
                siteName={selectedSiteName}
                deployment={selectedDeployment}
                deploymentId={dId}
                onGetDetails={() => {
                  setAudiomothPopupOpen(false);
                  navigate('/tools/bioacoustic', {
                    state: {
                      tab: 'acoustic',
                      deploymentId: dId,
                      siteId: selectedDeployment?.site_id,
                      projectId: projectDetails?.project_id,
                      siteName: selectedSiteName,
                      deploymentName: selectedDeployment?.deployment_name,
                      coordinates: selectedDeployment?.latitude && selectedDeployment?.longitude 
                        ? [selectedDeployment.latitude, selectedDeployment.longitude] 
                        : undefined,
                    },
                  });
                }}
                onUploadData={() => {
                  setAudiomothPopupOpen(false);
                  setUploadModalOpen(true);
                }}
                projectMedia={projectMedia}
                mediaUrls={mediaUrls}
              />
            );
          }

          return (
            <AudiomothDetectionsPopup
              open={audiomothPopupOpen}
              onClose={() => setAudiomothPopupOpen(false)}
              siteName={selectedSiteName}
              deployment={selectedDeployment}
              deploymentId={dId}
              onGetDetails={() => {
                setAudiomothPopupOpen(false);
                navigate('/tools/bioacoustic', {
                  state: {
                    tab: 'acoustic',
                    deploymentId: dId,
                    siteId: selectedDeployment?.site_id,
                    projectId: projectDetails?.project_id,
                    siteName: selectedSiteName,
                    deploymentName: selectedDeployment?.deployment_name,
                    coordinates: selectedDeployment?.latitude && selectedDeployment?.longitude 
                      ? [selectedDeployment.latitude, selectedDeployment.longitude] 
                      : undefined,
                  },
                });
              }}
              onUploadData={() => {
                setAudiomothPopupOpen(false);
                setUploadModalOpen(true);
              }}
              projectMedia={projectMedia}
              mediaUrls={mediaUrls}
            />
          );
        })()}

        <ShowcaseGalleryDialog
          open={showcaseModalOpen}
          onClose={() => setShowcaseModalOpen(false)}
          isLoadingCandidates={isLoadingCandidates}
          candidates={candidates}
          candidatePage={candidatePage}
          setCandidatePage={setCandidatePage}
          totalCandidates={totalCandidates}
          selectedCandidates={selectedCandidates}
          setSelectedCandidates={setSelectedCandidates}
          candidateCaptions={candidateCaptions}
          setCandidateCaptions={setCandidateCaptions}
          candidateFileNames={candidateFileNames}
          setCandidateFileNames={setCandidateFileNames}
          onSave={handleSaveShowcase}
        />

        <UploadAudioModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          deploymentId={selectedDeploymentId ?? selectedDeployment?.deployment_id}
        />
      </Box>

      {/* Reports & DataRoom Dialog */}
      <Dialog 
        open={activeLeftTab === 'reports' || activeLeftTab === 'dataroom'} 
        onClose={() => setActiveLeftTab('layers')}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            bgcolor: '#1a1a1a',
            backgroundImage: 'none',
            overflow: 'hidden',
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }
          }
        }}
      >
        <Box sx={{ 
          flex: 1, 
          height: '100%', 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          position: 'relative' 
        }}>
          <IconButton 
            onClick={() => setActiveLeftTab('layers')}
            sx={{ position: 'absolute', top: 12, right: 24, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 10 }}
          >
            <CloseIcon />
          </IconButton>
          
          {activeLeftTab === 'reports' && (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              overflow: 'hidden'
            }}>
              {projectDetails?.project_name?.toLowerCase().includes('tata motors') ? (
                <iframe
                  src="/TataMotors_Pimpri_Ecological%20Intelligence%20Deck.pptx.pdf#toolbar=0&navpanes=0&scrollbar=0"
                  width="100%"
                  height="100%"
                  style={{ border: 'none', display: 'block' }}
                  title="Tata Motors Report PDF"
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{ color: '#fff' }}>No reports available for this project.</Typography>
                </Box>
              )}
            </Box>
          )}
          
          {activeLeftTab === 'dataroom' && (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              overflowY: 'auto',
              pt: 6,
              boxSizing: 'border-box',
              bgcolor: '#070809',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: '#1a1a1a' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(16, 185, 129, 0.5)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: '#10b981' }
            }}>
              <DataRoom projectIdOverride={projectDetails.project_id} />
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
